import { useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { useQuery } from 'react-query';
import { makeStyles } from '@mui/styles';
import { translate } from '@openclassrooms/translations';
import ThumbnailSize from '../../../constants/ThumbnailSize';
import videoPlayerIconSrc from '../../../images/VideoPlayerIcon.svg';
import VimeoPlayer from './VimeoPlayer';
import HeroImage from '../HeroImage';

interface VimeoVideoMeta {
  // eslint-disable-next-line camelcase
  thumbnail_url: string;
  video_id: number;
}

const resolveThumbNailFromUrl = async (url: string): Promise<VimeoVideoMeta> => {
  const videoData = await fetch(
    `https://vimeo.com/api/oembed.json?url=${url}&width=1920&height=1080`
  ).then((res) => res.json());

  return videoData;
};

const useStyles = makeStyles(({ breakpoints }) => ({
  wrapper: {
    position: 'relative',
    width: ThumbnailSize.Default,
    [breakpoints.up('sm')]: {
      width: ThumbnailSize.OverSm,
    },
    [breakpoints.up('md')]: {
      width: ThumbnailSize.OverMd,
    },
    [breakpoints.up('lg')]: {
      width: ThumbnailSize.OverXl,
    },
  },
  thumbnailWrapper: {
    borderRadius: '50%',
    overflow: 'hidden',
    position: 'relative',
    '& img': {
      position: 'absolute',
      height: '120%',
      transform: 'translateX(-140px)', // Centers the Python course image (temporary fix)
    },
    height: ThumbnailSize.Default,
    [breakpoints.up('sm')]: {
      height: ThumbnailSize.OverSm,
    },
    [breakpoints.up('md')]: {
      height: ThumbnailSize.OverMd,
    },
    [breakpoints.up('lg')]: {
      height: ThumbnailSize.OverXl,
    },
  },
  playBtn: {
    border: 'none',
    borderRadius: '50%',
    outlineOffset: -2,
    background: 'none',
    position: 'absolute',
    top: 0,
    height: '100%',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg': {
      height: 56,
      width: 56,
    },
    cursor: 'pointer',
  },
}));

export interface HeroVideoProps {
  url: string;
  title: string;
  fallbackImgUrl: string;
}

function HeroVideo({ url, title, fallbackImgUrl }: HeroVideoProps) {
  const classes = useStyles();
  const [showVideo, setShowVideo] = useState(false);
  const { data, isError } = useQuery(['thumbnails', url], () => resolveThumbNailFromUrl(url));

  if (isError) {
    return <HeroImage src={fallbackImgUrl} />;
  }

  return (
    <>
      <div className={classes.wrapper} data-testid="hero-video">
        <div className={classes.thumbnailWrapper}>
          <img src={data?.thumbnail_url} alt="" />
        </div>
        <button className={classes.playBtn} type="button" onClick={() => setShowVideo(true)}>
          <img src={videoPlayerIconSrc} alt={translate('global.word.play_video')} />
        </button>
      </div>
      {data?.video_id && (
        <VimeoPlayer
          id={data.video_id}
          isOpened={showVideo}
          onClose={() => setShowVideo(false)}
          title={title}
        />
      )}
    </>
  );
}

export default HeroVideo;
